import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { amenities } from '../api/endpoints.js';
import { type AmenityDetail } from '../api/schemas.js';

export function AmenityDetailPage(): React.JSX.Element {
  const id = useParams().amenityId ?? '';
  const query = amenities.useItem(id);
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = amenities.useSoftDelete();

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.AMENITY_READ}
      parent={{ to: '/amenities', label: 'Amenities' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.AMENITY_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/amenities/${id}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.AMENITY_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data: AmenityDetail) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete amenity"
            description={`${data.name} will be retired from the catalogue. Existing assignments and history are retained.`}
            confirmLabel="Delete"
            destructive
            loading={remove.isPending}
            onConfirm={() => {
              void remove.mutateAsync(id).then(() => {
                setConfirmingDelete(false);
                void navigate('/amenities');
              });
            }}
          />
          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Type">{data.type}</DetailField>
            <DetailField label="Category">{data.category}</DetailField>
            <DetailField label="Icon URL">{data.iconUrl}</DetailField>
            <DetailField label="Description">{data.description}</DetailField>
            <DetailField label="Version">{data.version}</DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
